// Shared per-agent guardrail editor (Batch F1/F5/F6) — the ONE honest control
// set, rendered in both the Detail-Viewer Graph-tab drawer (F2) and the Builder
// canvas guard-node config panel (F3). Every control here maps to a real backend
// key that graph_nodes forwards into the guard config bag:
//   input:  enabled · pii_detection · pii_action · injection_detection ·
//           blocked_topics · on_block
//   output: enabled · prohibited_phrases · blocked_topics · on_block
// The old decorative toggles (a wrong-key injection flag + two grounding/
// compliance checkboxes) are GONE — grounding is the Fact Checker's job.
//
// Controlled: `config` is the current fc[which] object; `onSaveField(key, value)`
// persists one field (the drawer saves to the agent immediately, the Builder
// updates its node-config state). A tenant-wide FLOOR (Admin → Guardrails &
// Safety) is unioned on top of this at runtime and can only tighten it.

import ChipInput from "../../../../components/ChipInput";
import { COLORS } from "../../../../theme/colors";

const BORDER = COLORS.border || "#e2e5eb";
const DIM = COLORS.textDim || "#9090a8";
const ACCENT = COLORS.accent || "#3b82f6";
const RED = COLORS.red || "#ef4444";

const labelStyle = { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: DIM, display: "block", marginBottom: 7 };
const helpStyle = { fontSize: 11.5, color: DIM, margin: "4px 0 0", lineHeight: 1.5 };
const SELECT = { fontSize: 13, padding: "6px 9px", borderRadius: 8, border: `1px solid ${BORDER}`, background: COLORS.surface, color: COLORS.text, width: "100%" };
const row = { marginBottom: 14 };

function Toggle({ checked, disabled, onChange, label }) {
  const on = !!checked;
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={() => onChange(!on)} style={{ position: "relative", width: 44, height: 24, flex: "none", borderRadius: 999, border: `1px solid ${on ? ACCENT : BORDER}`, background: on ? ACCENT : (COLORS.surfaceHover || "#f1f5f9"), cursor: disabled ? "not-allowed" : "pointer", padding: 0, opacity: disabled ? 0.55 : 1, transition: "background .15s, border-color .15s" }}>
      <span style={{ position: "absolute", top: 2, left: on ? 22 : 2, width: 18, height: 18, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,.3)", transition: "left .15s" }} />
    </button>
  );
}

function ToggleRow({ label, checked, disabled, onChange, help }) {
  return (
    <div style={row}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{label}</span>
        <Toggle checked={checked} disabled={disabled} onChange={onChange} label={label} />
      </div>
      {help && <p style={helpStyle}>{help}</p>}
    </div>
  );
}

export default function GuardrailFields({ which, config, canEdit = true, onSaveField }) {
  const g = config || {};
  const isInput = which === "input_guardrails";
  const enabled = g.enabled !== false;
  const onBlock = g.on_block || {};
  const save = (k, v) => { if (canEdit && onSaveField) onSaveField(k, v); };
  const saveOnBlock = (patch) => save("on_block", { ...onBlock, ...patch });

  return (
    <div style={{
      padding: 14, borderRadius: 10, border: `1px solid ${BORDER}`, background: COLORS.surface,
      display: "flex", flexDirection: "column", gap: 2,
    }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text, marginBottom: 10 }}>
        {isInput ? "Input guardrails — what customers send" : "Output guardrails — what the agent says"}
      </div>

      <ToggleRow label="Enabled" checked={enabled} disabled={!canEdit}
        onChange={(v) => save("enabled", v)}
        help={isInput ? "Run these checks on every incoming message." : "Run these checks on every reply before it's sent."} />

      {isInput && (
        <>
          <ToggleRow label="PII detection" checked={!!g.pii_detection} disabled={!canEdit}
            onChange={(v) => save("pii_detection", v)}
            help="Detect cards, SSNs, emails, phones in the message." />
          {g.pii_detection && (
            <div style={row}>
              <label style={labelStyle}>PII handling</label>
              <select value={g.pii_action || "mask"} disabled={!canEdit} style={SELECT}
                onChange={(e) => save("pii_action", e.target.value)}>
                <option value="mask">Mask — redact detected PII (recommended)</option>
                <option value="block">Block — refuse the whole message</option>
                <option value="flag">Flag — record only, don't alter</option>
              </select>
            </div>
          )}
          <ToggleRow label="Prompt-injection detection" checked={!!g.injection_detection} disabled={!canEdit}
            onChange={(v) => save("injection_detection", v)}
            help={'Catches "ignore previous instructions"-style attacks.'} />
        </>
      )}

      {!isInput && (
        <div style={row}>
          <label style={labelStyle}>Prohibited phrases</label>
          <ChipInput value={g.prohibited_phrases || []} onChange={(v) => save("prohibited_phrases", v)}
            placeholder="phrases the agent must never say — Enter to add" disabled={!canEdit} tone={RED} />
          <p style={helpStyle}>A reply containing any of these is replaced with a safe fallback and flagged — the customer never sees the flagged text.</p>
        </div>
      )}

      <div style={row}>
        <label style={labelStyle}>Blocked topics</label>
        <ChipInput value={g.blocked_topics || []} onChange={(v) => save("blocked_topics", v)}
          placeholder="e.g. weapons, self-harm — Enter to add" disabled={!canEdit} tone={RED} />
        <p style={helpStyle}>Refused on {isInput ? "the way in" : "the way out"}. Adds to your workspace-wide floor (Admin → Guardrails &amp; Safety).</p>
      </div>

      <div style={{ ...row, display: "flex", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 180px" }}>
          <label style={labelStyle}>When blocked</label>
          <select value={onBlock.action || "message"} disabled={!canEdit} style={SELECT}
            onChange={(e) => saveOnBlock({ action: e.target.value })}>
            <option value="message">Show a safe message</option>
            <option value="connect_to_human">Connect to a human (escalate)</option>
            <option value="both">Both — message and escalate</option>
          </select>
        </div>
        <div style={{ flex: "1 1 180px" }}>
          <label style={labelStyle}>Message (optional)</label>
          <input value={onBlock.message || ""} disabled={!canEdit}
            onChange={(e) => saveOnBlock({ message: e.target.value })}
            placeholder="Leave blank for the default"
            style={{ ...SELECT }} />
        </div>
      </div>

      {!isInput && (
        <p style={{ ...helpStyle, marginTop: 2 }}>
          Grounding &amp; hallucination are handled by the <strong>Fact Checker</strong> (Overview → Fact Checker), not here.
        </p>
      )}
    </div>
  );
}
